import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { actions as currentUserActions } from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import { useApiAccountHomeAddressUpdateCreate } from '@rover/rsdk/src/apiClient/latest/api-account-home-address-update-create';
import { getApiCurrentUserRetrieveQueryKey } from '@rover/rsdk/src/apiClient/latest/api-current-user-retrieve';
import type { CurrentUserViewSchema } from '@rover/rsdk/src/apiClient/latest/model/currentUserViewSchema';
import type { HomeAddressUpdate } from '@rover/rsdk/src/apiClient/latest/model/homeAddressUpdate';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';
import type { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useDucks from '@rover/shared/js/hooks/useDucks';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { formatAddress } from '../utils';

interface ErrorResponse {
  errors?: Record<string, string[]>;
}

type SaveAddressParams = {
  data: HomeAddressUpdate;
  onSuccess?: (newAddressData: UserInformationAddress | null) => void;
  onError?: () => void;
};

type UseHomeAddress = {
  formattedAddress: string | null | void;
  address: UserInformationAddress;
  enabled: boolean;
  saveAddress: (params: SaveAddressParams) => void;
  isLoading: boolean;
  submissionErrors: Record<string, string[]>;
};

/**
 * Hook to get the authenticated user's home address and save functionality
 * Returns formatted address string, address object, save method, loading state, and errors
 * Returns null values for formattedAddress and address if user is not authenticated
 * or if the user is not in the variant experience.
 */
const useHomeAddress = (forceUseHomeAddress: boolean = false): UseHomeAddress => {
  const { user } = useCurrentUserShim();
  const rolloutDefaultToHomeAddress = useFlag(FLAGS.ROLLOUT_DEFAULT_TO_HOME_ADDRESS);
  const address = user?.address || null;
  const queryClient = useQueryClient();
  const [submissionErrors, setSubmissionErrors] = useState<Record<string, string[]>>({});

  // Get Redux actions for updating current user state
  const { data: reduxActions } = useDucks({
    actions: { fetchCurrentUserSuccess: currentUserActions.fetchCurrentUserSuccess },
  });

  const formattedAddress = useMemo(() => {
    return formatAddress(address);
  }, [address]);

  // API mutation hook for updating home address
  const { mutate, isLoading } = useApiAccountHomeAddressUpdateCreate();

  const saveAddress = ({ data, onSuccess, onError }: SaveAddressParams): void => {
    // Clear previous errors
    setSubmissionErrors({});

    mutate(
      { data },
      {
        onSuccess: (responseData) => {
          emitMetric('search-edit-home-address-save', { source: 'web' });
          fireDataLayerEvent({ event: 'search-edit-home-address-save' });

          const newAddressData = address
            ? {
                ...address,
                addressLine1: responseData.addressLine1,
                addressLine2: responseData.addressLine2,
                city: responseData.city,
                state: responseData.state,
                zip: responseData.zip,
                countryCode: responseData.countryCode,
                latitude: responseData.latitude,
                longitude: responseData.longitude,
              }
            : null;

          // Update Redux state for legacy pages that don't use React Query
          // Get the current React Query cache data and manually update it with the new address
          const currentUserData = queryClient.getQueryData(
            getApiCurrentUserRetrieveQueryKey()
          ) as CurrentUserViewSchema;
          if (
            currentUserData &&
            currentUserData.authenticated &&
            'currentUser' in currentUserData
          ) {
            const updatedUserData: CurrentUserViewSchema = {
              ...currentUserData,
              currentUser: {
                ...currentUserData.currentUser,
                address: newAddressData,
              },
            };
            reduxActions.fetchCurrentUserSuccess(updatedUserData);
          }

          // Call the provided onSuccess callback
          if (onSuccess) {
            onSuccess(newAddressData);
          }
        },

        onError: (error: ErrorType<Error>) => {
          if ('rawResponse' in error && error.rawResponse) {
            const errorData = error.rawResponse.data as ErrorResponse;
            if (errorData?.errors) {
              setSubmissionErrors(errorData.errors);
            }
          }

          // Call the provided onError callback
          if (onError) {
            onError();
          }
        },
      }
    );
  };

  const shouldUseHomeAddress = forceUseHomeAddress || Boolean(rolloutDefaultToHomeAddress);

  if (!shouldUseHomeAddress) {
    return {
      formattedAddress: null,
      address: null,
      enabled: false,
      saveAddress,
      isLoading,
      submissionErrors,
    };
  }

  return {
    formattedAddress,
    address,
    enabled: true,
    saveAddress,
    isLoading,
    submissionErrors,
  };
};

export default useHomeAddress;
